import providerDark from '@/assets/figma/provider-dark.svg';
import providerLight from '@/assets/figma/provider-light.svg';
import type { SocialProvider } from '@/entities/auth/api/auth.api';
import { startSocialLogin } from '@/entities/auth/social/socialRedirect';
import { cn } from '@/shared/lib/utils';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';

/** 마지막으로 성공한 수단 — 브라우저에만 저장하고 서버에 올리지 않는다 */
export const LAST_PROVIDER_KEY = 'rudalink-web:last-login-provider';

/** 카카오 → 네이버 → Google → Apple (국내 사용률 기준) */
const PROVIDERS: Array<{
  id: SocialProvider;
  label: string;
  className: string;
  icon: string;
}> = [
  {
    id: 'kakao',
    label: '카카오로 시작하기',
    className: 'border-[#fee500] bg-[#fee500] text-[#191600]',
    icon: providerDark,
  },
  {
    id: 'naver',
    label: '네이버로 시작하기',
    className: 'border-[#03c75a] bg-[#03c75a] text-white',
    icon: providerLight,
  },
  {
    id: 'google',
    label: 'Google로 시작하기',
    className: 'border-line-strong bg-surface text-ink',
    icon: providerDark,
  },
  {
    id: 'apple',
    label: 'Apple로 시작하기',
    className: 'border-black bg-black text-white',
    icon: providerLight,
  },
];

/** 로그인에 성공한 수단을 기록한다. 이메일로 성공하면 SNS 칩은 사라진다 (M-01) */
// eslint-disable-next-line react-refresh/only-export-components
export const rememberLastProvider = (provider: SocialProvider | 'email') => {
  try {
    localStorage.setItem(LAST_PROVIDER_KEY, provider);
  } catch {
    // 저장소를 못 쓰는 환경이면 칩만 안 보인다
  }
};

const readLastProvider = (): string | null => {
  try {
    return localStorage.getItem(LAST_PROVIDER_KEY);
  } catch {
    return null;
  }
};

export const SocialLoginButtons = ({
  className,
  pendingProvider,
  onPendingChange,
  onError,
  redirectTo,
}: {
  className?: string;
  pendingProvider: string | null;
  onPendingChange: (provider: string | null) => void;
  /** 인가 화면으로 보내지 못했을 때 (클라이언트 ID 미설정 등) — X1 */
  onError: (provider: SocialProvider) => void;
  /** 로그인 후 돌아갈 내부 경로 — 콜백까지 들고 간다 */
  redirectTo?: string;
}) => {
  const lastProvider = readLastProvider();

  // 제공자 화면에서 뒤로 가기로 돌아오면 bfcache가 진행 중 상태를 그대로 복원하므로 푼다
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) =>
      e.persisted && onPendingChange(null);
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, [onPendingChange]);

  /** 제공자 화면으로 이동한다. 결과는 /auth/callback/{provider}에서 처리한다 */
  const handleClick = async (provider: SocialProvider) => {
    onPendingChange(provider);
    try {
      await startSocialLogin(provider, redirectTo);
    } catch {
      onPendingChange(null);
      onError(provider);
    }
  };

  return (
    <div className={cn('flex flex-col gap-2.5', className)}>
      {PROVIDERS.map((p) => {
        const isPending = pendingProvider === p.id;
        return (
          <button
            key={p.id}
            type="button"
            disabled={pendingProvider !== null}
            onClick={() => void handleClick(p.id)}
            className={cn(
              'flex h-[52px] w-full items-center justify-center gap-2.5 rounded-md border px-5 text-[15px] leading-[23px] font-medium disabled:opacity-60',
              p.className,
            )}
          >
            {isPending ? (
              <Loader2 className="size-5 animate-spin" aria-hidden />
            ) : (
              <img src={p.icon} alt="" width={20} height={20} />
            )}
            {p.label}
            {lastProvider === p.id && (
              <span className="rounded-badge bg-ink px-2 py-[3px] text-[11px] leading-[17px] text-white">
                최근 사용
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};
